import React, { useEffect } from "react";
import { useState } from "react";
import { DropDownList } from "@progress/kendo-react-dropdowns";

/**
 * Regular dropdown renderer component (single selection)
 * @param {Object} props - Component props
 * @param {Object} props.element - Element object
 * @param {Array} props.filteredOptions - Filtered options array
 * @param {boolean} props.showResponses - Whether showing responses mode
 * @param {boolean} props.showValidationError - Whether to show validation error
 * @param {boolean} props.hasChoosen - Whether user has made a choice
 * @param {Function} props.handleFilterChange - Filter change handler
 * @param {Function} props.onChange - Change handler
 * @param {Function} props.setHasChoosen - Set has chosen function
 * @returns {JSX.Element} Regular dropdown component
 * @author Abhishek Dhillon
 */
const RegularDropdown = ({
  element,
  filteredOptions,
  showResponses,
  showValidationError,
  hasChoosen,
  handleFilterChange,
  resetFilter,
  onChange,
  setHasChoosen,
}) => {
  const { properties = {}, value = null } = element;
  const defaultValue = properties["default"] ? properties["default"] : null;
  const { required = false } = properties;
  const getSelectedValue = (val) => {
    if (typeof val === "object" && val) {
      return val;
    }
    return filteredOptions.find((option) => option?.value === val);
  };
  const [selectedValue, setSelectedValue] = useState(getSelectedValue(value));
  const [filterValue, setFilterValue] = useState("");

  /**
   * Handle filter change internally
   * @returns {void}
   * @author Sarthak Arora
   */
  const handleFilterChangeInternal = (event) => {
    setFilterValue(event.filter?.value || "");
    handleFilterChange(event);
  };

  useEffect(() => {
    if (value && onChange) {
      onChange(getSelectedValue(value));
    }
  }, []);

  return (
    <div className='d-flex w-100'>
      <DropDownList
        textField='label'
        valueField='value'
        value={selectedValue}
        defaultValue={getSelectedValue(defaultValue)}
        data={filteredOptions}
        onChange={(e) => {
          setSelectedValue(e.target.value);
          onChange && onChange(e.target.value);
          setHasChoosen(true);
        }}
        disabled={showResponses}
        className={`dropdown-input ${showValidationError && !hasChoosen ? "k-invalid" : ""}`}
        required={required}
        filterable={true}
        filter={filterValue}
        onFilterChange={handleFilterChangeInternal}
        onClose={() => {
          setFilterValue("");
          resetFilter && resetFilter();
        }}
      />
      {required && <span className='required-asterisk text-danger ms-1 ml-1'>*</span>}
    </div>
  );
};

export default RegularDropdown;
